import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface ContractorPartnershipPageProps {
    onBack: () => void;
}

interface ContractorFormData {
    company: string;
    name: string;
    email: string;
    phone: string;
    specialization: string;
    buildingTypes: string;
}

const ContractorPartnershipPage: React.FC<ContractorPartnershipPageProps> = ({ onBack }) => {
    const [formData, setFormData] = useState<ContractorFormData>({
        company: '',
        name: '',
        email: '',
        phone: '',
        specialization: '',
        buildingTypes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');
        
        try {
            const partnershipData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                company: formData.company,
                partnershipType: 'contractor' as const,
                serviceArea: formData.specialization,
                experience: formData.buildingTypes,
                certifications: '',
                website: '',
                additionalInfo: `Specialization: ${formData.specialization}, Building Types: ${formData.buildingTypes}`
            };

            const response = await fetch('/api/partnership', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(partnershipData),
            });

            const result = await response.json();

            if (response.ok) {
                setSubmitStatus('success');
                // Reset form on successful submission
                setFormData({
                    company: '',
                    name: '',
                    email: '',
                    phone: '',
                    specialization: '',
                    buildingTypes: ''
                });
            } else {
                console.error('Form submission error:', result.error);
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Network error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Contractor Network Program</h1>
                    <p className="text-xl text-gray-600 mb-4">
                        Join our vetted network of service providers for NYC's growing LL97 retrofit market.
                    </p>
                </div>

                {/* Hero Section */}
                <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-xl shadow-xl mb-12 overflow-hidden">
                    <div className="md:flex items-center">
                        <div className="md:w-1/2 p-8 md:p-12">
                            <h2 className="text-white text-3xl font-bold mb-4">Grow Your Business with LL97 Retrofits</h2>
                            <p className="text-green-100 text-lg mb-6">
                                Partner with us to access a steady stream of pre-qualified building owners needing LL97 compliance retrofits.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-center">
                                    <div className="bg-white rounded-full p-1 mr-3">
                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-white">$300M+ retrofit projects in pipeline</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="bg-white rounded-full p-1 mr-3">
                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-white">1,000+ buildings seeking retrofit services</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="bg-white rounded-full p-1 mr-3">
                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-white">Pre-qualified leads with financing in place</span>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2 bg-gradient-to-br from-blue-50 to-green-50 p-6 hidden md:block">
                            <div className="w-full rounded-lg shadow-lg bg-white p-8 flex flex-col items-center justify-center text-center">
                                <div className="text-blue-600 mb-4">
                                    <svg className="w-24 h-24 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">Professional Retrofit Team</h3>
                                <p className="text-gray-600 text-sm">Certified contractors specializing in energy-efficient building retrofits and sustainable construction practices.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {/* Left Column */}
                    <div className="col-span-2 space-y-8">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Why Join Our Network</h2>
                            <div className="space-y-6">
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="bg-green-100 rounded-full p-2">
                                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Access Pre-Qualified Building Owners</h3>
                                        <p className="mt-1 text-gray-600">
                                            We've identified thousands of NYC buildings that need immediate LL97 retrofits, with owners who understand 
                                            the compliance requirements and have budgeted for the work.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="bg-green-100 rounded-full p-2">
                                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Financing Already in Place</h3>
                                        <p className="mt-1 text-gray-600">
                                            Our building owners come with financing solutions already arranged through our lender partners, 
                                            eliminating payment concerns and project delays.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="bg-green-100 rounded-full p-2">
                                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Technical Support & Resources</h3>
                                        <p className="mt-1 text-gray-600">
                                            Our platform provides detailed building specifications, energy modeling, and compliance requirements, 
                                            streamlining your estimation and proposal process.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="bg-green-100 rounded-full p-2">
                                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Network & Collaboration</h3>
                                        <p className="mt-1 text-gray-600">
                                            Join a community of retrofit specialists, access co-bidding opportunities for larger projects, 
                                            and share resources to compete more effectively in the growing green building market.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Network Specialties</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        <h3 className="font-semibold text-gray-900">HVAC Modernization</h3>
                                    </div>
                                    <p className="text-gray-700">Electrification, heat pumps, VRF systems, and BMS integration for large commercial and multifamily buildings.</p>
                                </div>
                                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        <h3 className="font-semibold text-gray-900">Building Envelope</h3>
                                    </div>
                                    <p className="text-gray-700">Window replacements, insulation upgrades, air sealing, and façade improvements to reduce energy loss.</p>
                                </div>
                                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        <h3 className="font-semibold text-gray-900">Lighting & Controls</h3>
                                    </div>
                                    <p className="text-gray-700">LED conversions, smart lighting controls, occupancy/daylight sensors, and integration with building systems.</p>
                                </div>
                                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        <h3 className="font-semibold text-gray-900">Renewable Energy</h3>
                                    </div>
                                    <p className="text-gray-700">Solar PV installation, energy storage, and green roof systems for urban buildings.</p>
                                </div>
                                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        <h3 className="font-semibold text-gray-900">Water Systems</h3>
                                    </div>
                                    <p className="text-gray-700">Domestic hot water electrification, water-saving fixtures, and efficient distribution systems.</p>
                                </div>
                                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        <h3 className="font-semibold text-gray-900">Energy Management</h3>
                                    </div>
                                    <p className="text-gray-700">Building automation systems, energy monitoring, and smart controls for optimized building performance.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Call to Action */}
                    <div>
                        <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Join Our Network</h3>
                                <p className="text-gray-600">Complete the form to become a preferred contractor in our retrofit partner network.</p>
                            </div>
                            
                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                                    <input 
                                        type="text" 
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                                    <input 
                                        type="text" 
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input 
                                        type="email" 
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <input 
                                        type="tel" 
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                                    <select 
                                        name="specialization"
                                        value={formData.specialization}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                        <option value="">Select specialization</option>
                                        <option value="HVAC Modernization">HVAC Modernization</option>
                                        <option value="Building Envelope">Building Envelope</option>
                                        <option value="Lighting & Controls">Lighting & Controls</option>
                                        <option value="Renewable Energy">Renewable Energy</option>
                                        <option value="Water Systems">Water Systems</option>
                                        <option value="Energy Management">Energy Management</option>
                                        <option value="Multiple Services">Multiple Services</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Building Types</label>
                                    <select 
                                        name="buildingTypes"
                                        value={formData.buildingTypes}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                        <option value="">Select building types</option>
                                        <option value="Multifamily (5+ units)">Multifamily (5+ units)</option>
                                        <option value="Commercial Office">Commercial Office</option>
                                        <option value="Retail">Retail</option>
                                        <option value="Mixed-Use">Mixed-Use</option>
                                        <option value="Institutional">Institutional</option>
                                        <option value="All Building Types">All Building Types</option>
                                    </select>
                                </div>
                                
                                {submitStatus === 'success' && (
                                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                                        Thank you! Your application has been submitted successfully. We'll contact you within 48 hours.
                                    </div>
                                )}
                                
                                {submitStatus === 'error' && (
                                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                                        There was an error submitting your application. Please try again.
                                    </div>
                                )}
                                

                                
                                <p className="text-xs text-gray-500 text-center mt-2">
                                    A member of our contractor relations team will contact you within 48 hours to discuss next steps.
                                </p>
                            </form>
                            
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <div className="bg-gray-100 rounded-lg p-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Benefits At a Glance</h4>
                                    <ul className="space-y-2 text-sm text-gray-700">
                                        <li className="flex items-start">
                                            <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span>Pre-qualified leads with no marketing costs</span>
                                        </li>
                                        <li className="flex items-start">
                                            <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span>Guaranteed payment through project escrows</span>
                                        </li>
                                        <li className="flex items-start">
                                            <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span>Technical support & detailed specifications</span>
                                        </li>
                                        <li className="flex items-start">
                                            <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span>Featured in our building owner marketplace</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contractor Success Stories */}
                <div className="bg-gray-100 rounded-lg p-8 mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Contractor Success Stories</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center mb-4">
                                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1zm1-4a1 1 0 100 2h.01a1 1 0 100-2H7zm2 0a1 1 0 100 2h.01a1 1 0 100-2H9zm2 0a1 1 0 100 2h.01a1 1 0 100-2H11z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <h4 className="font-semibold">Metro Mechanical</h4>
                                    <p className="text-sm text-gray-600">HVAC Contractor</p>
                                </div>
                            </div>
                            <p className="text-gray-700">"Since joining the network, we've secured 12 major retrofit projects totaling over $7M. The pre-qualified leads and smooth financing process have transformed our business."</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center mb-4">
                                <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.477.859h4z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <h4 className="font-semibold">GreenLight Solutions</h4>
                                    <p className="text-sm text-gray-600">Lighting Specialist</p>
                                </div>
                            </div>
                            <p className="text-gray-700">"We've grown from a 5-person shop to a team of 23 in just 18 months. The technical support and building specifications save us countless hours in the proposal process."</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center mb-4">
                                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <h4 className="font-semibold">EnvelopePro</h4>
                                    <p className="text-sm text-gray-600">Building Envelope Specialist</p>
                                </div>
                            </div>
                            <p className="text-gray-700">"The collaborative approach has allowed us to partner with HVAC contractors on comprehensive retrofits we couldn't have handled alone. Our revenue has doubled year over year."</p>
                        </div>
                    </div>
                </div>

                {/* CTA Footer */}
                <div className="bg-green-800 rounded-xl p-8 text-center">
                    <h2 className="text-2xl font-bold text-white mb-4">Ready to Grow Your Business with LL97 Retrofits?</h2>
                    <p className="text-green-100 mb-6 max-w-3xl mx-auto">
                        Join our network of specialized contractors today and gain access to qualified projects, financing support, and technical resources.
                    </p>
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-white text-green-800 hover:bg-green-100 disabled:bg-gray-200 py-3 px-8 rounded-lg font-medium transition-colors"
                    >
                        {isSubmitting ? 'Submitting...' : 'Apply Now'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContractorPartnershipPage;
